/* eslint-disable no-param-reassign, no-useless-catch */
import appSettings from 'wanderu-config';
import Logger from 'wanderu-logger';
import { SnowplowService } from 'wanderu-service-analytics';
import { WapiService } from 'wanderu-service-wapi';
import { compressWhitespace } from 'wanderu-utils';

import {
    UserAlreadyExistsError,
    ExpiredCodeError,
    LimitExceededError,
    InactiveAccountError,
    InvalidPasswordError,
    LoginFailedUseThirdPartyError,
    GeneralLoginError,
} from '../Errors';
import {
    USER_NOT_FOUND,
    USERNAME_EXISTS,
    INVALID_PARAMETER,
    INVALID_PASSWORD,
    LIMIT_EXCEEDED,
    NOT_AUTHORIZED,
    GENERAL_SIGNUP_ERROR,
} from '../constants';
import Events from '../events/AccountServiceEvents';
import {
    checkAccountInfoValid,
    clearLocalStorageCredentials,
} from '../utils';

const logger = Logger.getLogger('CognitoService');

async function createAccount(createAccountInfo) {
    createAccountInfo.firstName = compressWhitespace(createAccountInfo.firstName);
    createAccountInfo.lastName = compressWhitespace(createAccountInfo.lastName);
    createAccountInfo.email = compressWhitespace(createAccountInfo.email);

    let success = true;

    try {
        checkAccountInfoValid(createAccountInfo);

        return await WapiService.post('signup.json', {
            body: {
                email: createAccountInfo.email,
                password: createAccountInfo.password,
                firstName: createAccountInfo.firstName,
                lastName: createAccountInfo.lastName,
            },
        });
    } catch (ex) {
        success = false;
        logger.error('createAccount', ex);

        if (ex?.responseBody?.error?.message === USERNAME_EXISTS) {
            throw new UserAlreadyExistsError();
        }

        if (ex?.responseBody?.error?.message === GENERAL_SIGNUP_ERROR) {
            throw new GeneralLoginError();
        }

        throw ex;
    } finally {
        const cognitoSignUpEvent = Events.createSignUpEvent({
            provider: 'cognito',
            email: createAccountInfo.email,
            success,
        });

        SnowplowService.fireStructuredEvent(cognitoSignUpEvent);
    }
}

async function logIn(email, password, provider) {
    try {
        return await WapiService.startNewSession({
            provider,
            email,
            password,
        });
    } catch (ex) {
        throw ex;
    }
}

/**
 * Change the logged in user's password
 * @param currentPassword Previous password
 * @param newPassword New password
 */
async function changePassword(currentPassword, newPassword) {
    try {
        return await WapiService.post('password/update.json', {
            body: {
                currentPassword,
                newPassword,
            },
        });
    } catch (ex) {
        if (ex.name === INVALID_PASSWORD) {
            throw new InvalidPasswordError();
        }

        throw ex;
    }
}

/**
 * Begin forgot password flow.
 * @param email The user's email address
 */
async function forgotPassword(email) {
    email = compressWhitespace(email);

    try {
        return await WapiService.post('password/reset.json', { body: { email } });
    } catch (ex) {
        logger.info('forgotPassword', ex);

        if (ex.name === INVALID_PARAMETER) {
            throw new InactiveAccountError();
        }

        if (ex.name === LIMIT_EXCEEDED) {
            throw new LimitExceededError();
        }

        if (ex.name === NOT_AUTHORIZED) {
            throw new LoginFailedUseThirdPartyError();
        }

        throw ex;
    }
}

/**
 * Confirm forgot password.
 * @param email The user's email address
 * @param confirmationCode The confirmation code that the user provided
 * @param newPassword The users's new password
 */
async function forgotPasswordConfirmation(email, code, password) {
    email = compressWhitespace(email);

    try {
        return await WapiService.post('password/confirmation.json', {
            body: {
                email,
                code,
                password,
            },
        });
    } catch (ex) {
        throw new ExpiredCodeError();
    }
}

/**
 * checks to see if a Cognito account exists with the given email address.
 * BE CAREFUL WITH THIS - call it too many times in a short span of time with the same email
 * address and Cognito will temporarily lock access to that account if it exists.
 *
 * @param {String} emailAddress
 * @return {Boolean}
 * @async
 */
async function accountExistsForEmail(email) {
    const knownInvalidPassword = 'abc'; // length < min password length

    try {
        /* calls the auth.json to perform a look up of the provided email
         * without actually logging the user in, to see if the account already exists */
        await WapiService.postWithoutAuth('auth.json', {
            body: {
                credentials: {
                    ...appSettings.config.wapi.credentials,
                    type: 'email',
                    email,
                    password: knownInvalidPassword,
                },
            },
        }, true);
    } catch (ex) {
        return ex.code !== USER_NOT_FOUND;
    }

    // falling through to here means the login attempt was successful, which shouldn't happen
    logger.error('account-exists check resulted in actual login');
    clearLocalStorageCredentials();

    // ...but the account obviously exists, sooo
    return true;
}

export default {
    accountExistsForEmail,
    changePassword,
    createAccount,
    forgotPassword,
    forgotPasswordConfirmation,
    logIn,
};
